import { CommonModule } from '@angular/common';
import { ChangeDetectorRef, Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import {
  AbstractControl,
  FormsModule,
  ReactiveFormsModule,
  UntypedFormBuilder,
  UntypedFormGroup,
  Validators,
} from '@angular/forms';
import { ChangableComponent } from '@app/models/changable.component';
import { TinyMCEConfig, tinyMCEScriptSrc } from '@core/components/wyisiwyg-editor/tinymce.config';
import { CoreModule } from '@core/core.module';
import { AccountService, AuthService, ContentService, FilesService, PlatformService } from '@core/services';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { ErrorNotificationService } from '@shared/error-notification/error-notification.service';
import { IconComponent } from '@shared/icon/icon.component';
import { getMe } from '@store/reducers/profile.reducer';
import { EditorModule as TinymceModule, TINYMCE_SCRIPT_SRC } from '@tinymce/tinymce-angular';
import { Content, ContentTypeEnum, ImageSizeEnum, MicroText, User, UserStatusEnum } from 'lingo2-models';
import { OnUiButtonModule, OnUiButtonState, OnUiCover, OnUiCoverModule } from 'onclass-ui';
import { Observable } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

export interface IFeedPostFormComponentState {
  editorInFocus: boolean;
  saveButton: OnUiButtonState;
  publishButton: OnUiButtonState;
  editorLoaded: boolean;
  disabled: boolean;
}

@Component({
  selector: 'app-feed-post-form',
  standalone: true,
  imports: [
    CommonModule,
    CoreModule,
    FormsModule,
    OnUiCoverModule,
    OnUiButtonModule,
    ReactiveFormsModule,
    TinymceModule,
    IconComponent,
  ],
  providers: [{ provide: TINYMCE_SCRIPT_SRC, useValue: tinyMCEScriptSrc }],
  templateUrl: './feed-post-form.component.html',
  styleUrls: ['./feed-post-form.component.scss'],
})
export class FeedPostFormComponent extends ChangableComponent implements OnInit {
  public me: User;
  public form: UntypedFormGroup;
  public _item = new MicroText({ type: ContentTypeEnum.microtext, text: '' });
  public state: IFeedPostFormComponentState = {
    editorInFocus: false,
    saveButton: 'default',
    publishButton: 'default',
    editorLoaded: false,
    disabled: true,
  };
  public editor;
  public editorConfig = { ...TinyMCEConfig };
  public avatar: OnUiCover;
  @Input() classroomId: string;
  @Input() schoolId: string;
  @Output() posted = new EventEmitter<boolean>();

  constructor(
    public errorNotificationService: ErrorNotificationService,
    private authService: AuthService,
    private fb: UntypedFormBuilder,
    private contentService: ContentService,
    private translate: TranslateService,
    private store: Store,
    protected readonly platform: PlatformService,
    protected readonly cdr: ChangeDetectorRef,
  ) {
    super(cdr, platform);
  }

  ngOnInit() {
    this.createForm();
    this.store
      .select(getMe)
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: (me) => {
          this.me = me;
          this.avatar = {
            type: 'avatar',
            img: FilesService.getFileUrlBySize(this.me.userpic_id, ImageSizeEnum.sm),
            title: AccountService.getUserFullName(me),
            form: 'circle',
          };
        },
        error: (error) => {
          this.errorNotificationService.captureError(error, 'LOAD-SOMEDATA');
        },
      });
    this.form.valueChanges.pipe(takeUntil(this.destroyed$)).subscribe({
      next: () => {
        this.form.markAsDirty();
      },
      error: (error) => {
        this.errorNotificationService.captureError(error, 'OTHER-PROBLEM');
      },
    });
    this.configureEditor();
  }

  public get item(): Content {
    return this._item;
  }

  public get isGuest() {
    return this.me && this.me?.status === UserStatusEnum.guest;
  }

  protected createForm() {
    const _item = this._item || new MicroText();

    this.form = this.fb.group({
      text: [_item.text || '', [Validators.required, Validators.maxLength(20000)]],
    });
  }

  protected getValuesFromForm(): Partial<MicroText> {
    const { text } = this.form.value;

    return {
      id: this._item.id,
      type: this._item.type,
      text,
      classroom_id: this.classroomId,
      school_id: this.schoolId,
    };
  }

  public get text(): AbstractControl {
    return this.form.get('text');
  }

  protected resetButtonState(buttonState: string, timeout = 1500) {
    this.setTimeout(() => {
      this.state[buttonState] = 'default';
    }, timeout);
  }

  public get readyForSave(): boolean {
    return this.form.dirty && this.form.valid && this.state.saveButton === 'default';
  }

  private configureEditor() {
    this.translate
      .get('feed.post-form.placeholder')
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: (placeholder) => {
          this.editorConfig.placeholder = placeholder;
          this.state.editorLoaded = true;
        },
        error: (error) => {
          this.errorNotificationService.captureError(error, 'OTHER-PROBLEM');
        },
      });
  }

  protected resetForm() {
    this._item = new MicroText({ type: ContentTypeEnum.microtext, text: '' });
    this.createForm();
    this.form.markAsPristine();

    this.state.editorLoaded = false; // это чтобы сбросить редактор
    this.setTimeout(() => {
      this.state.editorLoaded = true;
    }, 50);
  }

  public async save() {
    try {
      await this._save();
      this.posted.emit(true);

      this.resetForm();
    } catch {}
  }

  protected _save(): Promise<boolean> {
    return new Promise((resolve, reject) => {
      if (!this.readyForSave) {
        reject();
        return;
      }

      const values = this.getValuesFromForm();
      if (!values) {
        reject();
        return;
      }

      const isNew = !values.id;
      this.state.saveButton = 'progress';
      const observable: Observable<Content> = isNew
        ? this.contentService.createContent(values)
        : this.contentService.updateContent(values.id, values);

      observable.pipe(takeUntil(this.destroyed$)).subscribe({
        next: (item) => {
          this._item.id = item.id;
          this.state.saveButton = 'done';
          this.resetButtonState('saveButton');
          resolve(true);
        },
        error: (error) => {
          this.state.saveButton = 'fail';
          this.resetButtonState('saveButton');
          this.errorNotificationService.captureError(error, 'SAVE-PROBLEM');
          reject(error);
        },
      });
    });
  }

  public cleartext() {
    this.text.patchValue('');
  }

  public authorize() {
    this.authService.showAuthModal();
  }

  public checkForChanges() {
    this.markForCheck();
  }
}
